import React, { useState } from 'react'
import { Typography } from '@material-ui/core'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import HeadTitle from '../../components/text/head-title'
import InputLabel from '../../components/text/input-label'
import CustomButtonComponent from '../../components/buttons/customButtonComponent/CustomButtonComponent'
import InternationalizedFormattedText from '../../components/util/internationalized-formatted-text'
import { getMarcaComercial, getRedirectUri, isJWTExpired } from '../../utils/utils'
import { changePasswordService } from '../../services/services'
import { verifyPassword } from '../../components/util/verification'
import {
  PASSWORD_RESET_STATUS_ERROR,
  PASSWORD_RESET_STATUS_EXPIRED,
  PASSWORD_RESET_STATUS_FORM,
  PASSWORD_RESET_STATUS_SUCCESS,
  RECAPTCHA_ACTION_PASSWORD_RESET,
} from './constants'
import RecaptchaInfo from '../../components/recaptcha/recaptcha-info'
import { BUTTON_SIZE_MEDIUM, PASSWORD_MAX_LENGTH, PASSWORD_SPECIAL_CHARS } from '../../utils/constants'
import { ROUTE_PASSWORD_RECOVERY } from '../../components/routing/routes'

const PasswordResetPage = ({ classes }) => {
  const intl = useIntl()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const brand = useSelector((state) => state.reducer.brand)
  const urlParams = new URLSearchParams(window.location.search)
  const [token] = useState(urlParams.get('token'))
  const redirectUri = getRedirectUri(brand?.abbreviation)

  const [status, setStatus] = useState(() =>
    isJWTExpired(token) ? PASSWORD_RESET_STATUS_EXPIRED : PASSWORD_RESET_STATUS_FORM
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!token) {
    return <Redirect to={ROUTE_PASSWORD_RECOVERY} />
  }

  const handleGoBack = (url) => {
    window.location.replace(url)
  }

  const changePasswordCallback = () => {
    setStatus(PASSWORD_RESET_STATUS_SUCCESS)
    setIsSubmitting(false)
  }

  const changePasswordErrorCallback = () => {
    setStatus(PASSWORD_RESET_STATUS_ERROR)
    setIsSubmitting(false)
  }

  const handleSubmit = (values) => {
    // El script de reCAPTCHA se carga de forma asincrona: sin el no se puede enviar la peticion
    if (!executeRecaptcha) return

    setIsSubmitting(true)
    executeRecaptcha(RECAPTCHA_ACTION_PASSWORD_RESET)
      .then((captchaToken) => {
        const body = {
          nuevaContrasena: values.password,
          jwt: token,
          captchaToken,
        }
        changePasswordService(
          getMarcaComercial(brand),
          body,
          {},
          changePasswordCallback,
          changePasswordErrorCallback
        )
      })
      .catch(() => {
        setIsSubmitting(false)
      })
  }

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required(
        <InternationalizedFormattedText variant='body2' id='passwordRecovery.newPasswordRequired' />
      )
      .test(
        'passwordStrength',
        <InternationalizedFormattedText variant='body2' id='passwordRecovery.passwordMustFollowTheRules' />,
        (value) => verifyPassword(value)
      ),
    confirmPassword: Yup.string()
      .required(
        <InternationalizedFormattedText variant='body2' id='passwordRecovery.newPasswordRepeatedRequired' />
      )
      .oneOf(
        [Yup.ref('password'), null],
        <InternationalizedFormattedText variant='body2' id='passwordRecovery.repeatedPasswordNotEqual' />
      ),
  })

  return (
    <div className={`${classes.pageContainer} ${classes.centeredPageContainer}`}>
      <div className={classes.loginWrapper}>
        <HeadTitle text='passwordRecovery.title' />
        {status === PASSWORD_RESET_STATUS_FORM && (
          <Formik
            initialValues={{ password: '', confirmPassword: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form>
                <div className={classes.gapContainer}>
                  <div className={classes.passwordRulesContainer}>
                    <InternationalizedFormattedText
                      variant='body2'
                      id='passwordRecovery.passwordInstructions'
                    />
                    <div className={classes.passwordRulesRow}>
                      <InternationalizedFormattedText
                        variant='body2'
                        id='passwordRecovery.passwordInstructionsSpecialChars'
                      />
                      <Typography variant='body2' className={classes.passwordRulesChars}>
                        <strong>{PASSWORD_SPECIAL_CHARS}</strong>
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.marginBottomForm}>
                    <InputLabel text='passwordRecovery.password' />
                    <Field
                      id='password'
                      name='password'
                      type='password'
                      maxLength={PASSWORD_MAX_LENGTH}
                      className={
                        errors.password && touched.password
                          ? 'form-control is-invalid'
                          : 'form-control'
                      }
                      placeholder={intl.formatMessage({
                        id: 'passwordRecovery.newPasswordRequired',
                      })}
                    />
                    {errors.password && touched.password && (
                      <div className='invalid-feedback'>{errors.password}</div>
                    )}
                  </div>
                  <div className={classes.marginBottomForm}>
                    <InputLabel text='passwordRecovery.passwordRepeated' />
                    <Field
                      id='confirmPassword'
                      name='confirmPassword'
                      type='password'
                      maxLength={PASSWORD_MAX_LENGTH}
                      className={
                        errors.confirmPassword && touched.confirmPassword
                          ? 'form-control is-invalid'
                          : 'form-control'
                      }
                      placeholder={intl.formatMessage({
                        id: 'passwordRecovery.newPasswordRepeatedRequired',
                      })}
                    />
                    {errors.confirmPassword && touched.confirmPassword && (
                      <div className='invalid-feedback'>{errors.confirmPassword}</div>
                    )}
                  </div>
                  <div
                    className={`${classes.ButtonContainer} ${classes.recoveryPasswordButtons}`}
                  >
                    <CustomButtonComponent
                      type='submit'
                      label={
                        isSubmitting
                          ? 'passwordRecovery.resetingPassword'
                          : 'passwordRecovery.submit'
                      }
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      loadingText='passwordRecovery.resetingPassword'
                      size={BUTTON_SIZE_MEDIUM}
                    />
                    <CustomButtonComponent
                      primary={false}
                      type='button'
                      label='form.cancelButton'
                      onClick={() => handleGoBack(redirectUri)}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
        {status === PASSWORD_RESET_STATUS_SUCCESS && (
          <div className={classes.gapContainer}>
            <InternationalizedFormattedText
              variant='body2'
              id='passwordReset.successMessage'
            />
            <div
              className={`${classes.ButtonContainer} ${classes.recoveryPasswordButtons}`}
            >
              <CustomButtonComponent
                type='button'
                label='accept'
                onClick={() => handleGoBack(redirectUri)}
              />
            </div>
          </div>
        )}
        {status === PASSWORD_RESET_STATUS_EXPIRED && (
          <div className={classes.gapContainer}>
            <InternationalizedFormattedText
              variant='body2'
              id='passwordReset.expiredLinkMessage'
            />
            <div
              className={`${classes.ButtonContainer} ${classes.recoveryPasswordButtons}`}
            >
              <CustomButtonComponent
                type='button'
                label='accept'
                onClick={() => handleGoBack(redirectUri)}
              />
            </div>
          </div>
        )}
        {status === PASSWORD_RESET_STATUS_ERROR && (
          <div className={classes.gapContainer}>
            <InternationalizedFormattedText
              variant='body2'
              id='passwordReset.errorMessage'
            />
            <div
              className={`${classes.ButtonContainer} ${classes.recoveryPasswordButtons}`}
            >
              <CustomButtonComponent
                type='button'
                label='accept'
                onClick={() => handleGoBack(redirectUri)}
              />
            </div>
          </div>
        )}
        <RecaptchaInfo />
      </div>
    </div>
  )
}

export default PasswordResetPage
